/**
 * Legacy Key Handling Utility
 *
 * This utility handles legacy token-based authentication logic for the CMS
 * Component Library. It's separated from the main OIDC debugger for better
 * test isolation and separation of concerns.
 */

import { OIDCEndpointTest, testOIDCEndpoint } from './OIDCDebugger';

/**
 * Detects if the current URL contains a token parameter
 */
export function hasTokenParameter(url: string): boolean {
  try {
    const urlObj = new URL(url);

    // Check query parameters
    if (urlObj.searchParams.has('token')) {
      return true;
    }

    // Check hash fragment for token parameter (e.g., #token=abc123)
    if (urlObj.hash) {
      const hashParams = new URLSearchParams(urlObj.hash.substring(1));
      if (hashParams.has('token')) {
        return true;
      }
    }

    return false;
  } catch {
    // Fallback for malformed URLs - use regex for more precise matching
    return /[?&#]token(?:=|$)/.test(url);
  }
}

/**
 * Determines the base API URL based on hostname
 */
export function getBaseApiUrl(hostname: string): string {
  if (hostname.includes('test')) {
    return 'https://api.cms-test.theinstitutes.org';
  }
  if (hostname.includes('sandbox')) {
    return 'https://api.cms-sandbox.theinstitutes.org';
  }
  return 'https://api.cms-prod.theinstitutes.org';
}

/**
 * Gets the list of endpoints to test based on environment and token presence
 */
export function getEndpointsToTest(hostname: string, url: string): string[] {
  const baseApiUrl = getBaseApiUrl(hostname);
  const endpoints = [`/internal/jwt`];

  // Only test keys-preview endpoint if token parameter is present
  // (keys-preview is only relevant for token-based authentication)
  if (hasTokenParameter(url)) {
    endpoints.push(`${baseApiUrl}/kontent-ai/keys-preview`);
  }

  return endpoints;
}

/**
 * Tests an endpoint and optionally tests POST if GET succeeds for OIDC endpoints
 */
export async function testEndpointWithConditionalPost(
  endpoint: string,
): Promise<OIDCEndpointTest[]> {
  const tests: OIDCEndpointTest[] = [];

  // Test GET request first
  const getTest = await testOIDCEndpoint(endpoint, 'GET');
  tests.push(getTest);

  // If GET succeeded (200-299) and this is an OIDC endpoint, also test POST
  if (
    endpoint.includes('/internal/jwt') &&
    getTest.status >= 200 &&
    getTest.status < 300
  ) {
    const postTest = await testOIDCEndpoint(endpoint, 'POST');
    tests.push(postTest);
  }

  return tests;
}

/**
 * Runs all endpoint tests with conditional POST logic
 */
export async function runLegacyEndpointTests(
  hostname: string,
  url: string,
): Promise<OIDCEndpointTest[]> {
  const endpoints = getEndpointsToTest(hostname, url);
  const allTests: OIDCEndpointTest[] = [];

  for (const endpoint of endpoints) {
    console.log(`🧪 Testing endpoint: ${endpoint}`);
    const tests = await testEndpointWithConditionalPost(endpoint);
    allTests.push(...tests);
  }

  return allTests;
}
